/**
 * @fileoverview Modal component for creating, editing, cloning, and managing visual themes for Esquivador (Surfer - Id: 51).
 * Features:
 * - Theme selector with quick preset / custom theme switching.
 * - Actions: "+ Nuevo Tema", "Clonar", "Eliminar", and "Guardar".
 * - Multi-tab organization:
 *   1. Identidad & Marca (Nombre, Logotipo de Marca, Colores Hex Primario y Secundario)
 *   2. Pista & Entorno (Fondo/Cielo 1920x1080, Pista/Calzada 1024x1024, Props Laterales 1024x1024, Cockpit 1920x1080)
 *   3. Corredores 1 vs 1 (Vehículo/Avatar P1, Vehículo/Avatar P2)
 *   4. Obstáculos & Coleccionables (Obstáculo 512x512, Moneda 512x512, Bonus Especial 512x512)
 * - Reusable Drag & Drop `FileDropzone` with SkiaSharp WebP upload (`UploadAsset`) and instant preview.
 * - Individual asset removal and clean state management.
 * 
 * @module components/Admin/Games/Surfer/SurferThemeModal
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import {
    CreateTheme,
    UpdateTheme,
    DeleteTheme,
    CloneTheme,
    UploadAsset,
    ResolveMediaUrl
} from '../../../../utilities/BL/Surfer';

/**
 * Resuelve y sanitiza la URL para previsualización multimedia.
 * @param {string|null} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL formateada.
 */
export const getFullAssetUrl = (mediaPath) => {
    return ResolveMediaUrl(mediaPath);
};

/**
 * Componente Drag & Drop reutilizable para la carga y preview de imágenes individuales.
 */
const FileDropzone = ({
    label,
    fieldName,
    assetType,
    value = '',
    onUpload,
    onRemove,
    isUploading = false,
    helpText = '',
    aspectHint = '',
    accept = 'image/png, image/jpeg, image/webp, image/gif, image/svg+xml',
    previewHeight = '85px'
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);
    const inputElementId = `surfer-dropzone-input-${fieldName}`;

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = (dragEvent) => {
        dragEvent.preventDefault();
        dragEvent.stopPropagation();
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        dropEvent.stopPropagation();
        setIsDragHoverActive(false);
        const droppedFiles = dropEvent.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            onUpload(droppedFiles[0], assetType, fieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFiles = changeEvent.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            onUpload(selectedFiles[0], assetType, fieldName);
            changeEvent.target.value = '';
        }
    };

    return (
        <div className="mb-3 surfer-dropzone-wrapper">
            <div className="d-flex justify-content-between align-items-center mb-1">
                {label && (
                    <label className="form-label fw-bold text-light small mb-0">
                        {label}
                    </label>
                )}
                {aspectHint && (
                    <span className="badge bg-dark text-info border border-info border-opacity-25 small font-monospace">
                        {aspectHint}
                    </span>
                )}
            </div>

            <div
                className={`border border-2 rounded-3 p-3 text-center position-relative transition-all d-flex flex-column align-items-center justify-content-center ${
                    isDragHoverActive
                        ? 'border-info bg-dark shadow-lg'
                        : 'border-secondary border-opacity-50 bg-black bg-opacity-40'
                }`}
                style={{
                    borderStyle: 'dashed',
                    minHeight: '110px',
                    cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) {
                        const inputEl = document.getElementById(inputElementId);
                        if (inputEl) inputEl.click();
                    }
                }}
            >
                <input
                    id={inputElementId}
                    type="file"
                    className="d-none"
                    accept={accept}
                    onChange={handleNativeFileChange}
                    disabled={isUploading}
                />

                {isUploading ? (
                    <div className="py-2 d-flex flex-column align-items-center">
                        <div className="spinner-border spinner-border-sm text-info mb-2" role="status"></div>
                        <span className="small text-info fw-bold">Optimizando a WebP con SkiaSharp...</span>
                    </div>
                ) : value ? (
                    <div
                        className="position-relative d-flex flex-column align-items-center w-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={getFullAssetUrl(value)}
                            alt={label || 'Vista previa'}
                            className="img-thumbnail shadow-sm bg-dark border-secondary mb-2"
                            style={{
                                maxHeight: previewHeight,
                                maxWidth: '100%',
                                objectFit: 'contain'
                            }}
                            onError={(errorEvent) => {
                                errorEvent.target.style.display = 'none';
                            }}
                        />
                        <div className="d-flex gap-2 align-items-center">
                            <span
                                className="small text-truncate text-secondary font-monospace"
                                style={{ maxWidth: '180px' }}
                                title={value}
                            >
                                {value.split('/').pop()}
                            </span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                title="Eliminar imagen"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(fieldName);
                                }}
                            >
                                <i className="bi bi-trash3"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-info py-0 px-2"
                                title="Reemplazar archivo"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const inputEl = document.getElementById(inputElementId);
                                    if (inputEl) inputEl.click();
                                }}
                            >
                                <i className="bi bi-arrow-repeat"></i>
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-info fs-3 d-block mb-1"></i>
                        <span className="small text-light text-opacity-75 d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-info">clic para explorar</strong>
                        </span>
                        {helpText && (
                            <small className="text-muted d-block mt-1 font-monospace" style={{ fontSize: '0.75rem' }}>
                                {helpText}
                            </small>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

FileDropzone.propTypes = {
    label: PropTypes.string,
    fieldName: PropTypes.string.isRequired,
    assetType: PropTypes.string.isRequired,
    value: PropTypes.string,
    onUpload: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    helpText: PropTypes.string,
    aspectHint: PropTypes.string,
    accept: PropTypes.string,
    previewHeight: PropTypes.string
};

/**
 * Estructura predeterminada para un nuevo tema de Esquivador.
 */
const DEFAULT_THEME_TEMPLATE = {
    id: null,
    name: 'Nuevo Tema Esquivador',
    isSystemPreset: false,
    brandLogoUrl: '',
    backgroundUrl: '',
    roadUrl: '',
    sidePropUrl: '',
    obstacleUrl: '',
    coinUrl: '',
    bonusUrl: '',
    player1Url: '',
    player2Url: '',
    cockpitUrl: '',
    primaryColor: '#00FFCC',
    secondaryColor: '#FF007F'
};

/**
 * Modal principal de configuración y administración de temas de Esquivador.
 */
export const SurferThemeModal = ({
    isOpen = false,
    onClose = () => {},
    themesList = [],
    activeThemeId = null,
    roomId = '',
    operatorId = '',
    onThemeSelected = () => {},
    onThemesRefreshed = () => {}
}) => {
    const { showMessage } = useFloatMessage();

    // Pestaña activa del modal ('general', 'track', 'players', 'collectibles')
    const [activeTab, setActiveTab] = useState('general');

    // Tema seleccionado actualmente en el modal
    const [selectedThemeState, setSelectedThemeState] = useState({ ...DEFAULT_THEME_TEMPLATE });
    const [selectedThemeId, setSelectedThemeId] = useState(null);

    // Estados de operaciones en curso
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [uploadingFields, setUploadingFields] = useState({});

    // Sincronizar tema seleccionado al abrir o cambiar la lista
    useEffect(() => {
        if (!isOpen) return;

        if (themesList && themesList.length > 0) {
            const current = themesList.find((t) => t.id === activeThemeId) || themesList[0];
            setSelectedThemeId(current.id);
            setSelectedThemeState({
                ...DEFAULT_THEME_TEMPLATE,
                ...current,
                primaryColor: current.primaryColor || '#00FFCC',
                secondaryColor: current.secondaryColor || '#FF007F'
            });
        } else {
            setSelectedThemeId(null);
            setSelectedThemeState({ ...DEFAULT_THEME_TEMPLATE });
        }
    }, [isOpen, themesList, activeThemeId]);

    // Cambiar de tema seleccionado en el selector del modal
    const handleSelectThemeChange = (themeId) => {
        const found = themesList.find((t) => String(t.id) === String(themeId));
        if (found) {
            setSelectedThemeId(found.id);
            setSelectedThemeState({
                ...DEFAULT_THEME_TEMPLATE,
                ...found,
                primaryColor: found.primaryColor || '#00FFCC',
                secondaryColor: found.secondaryColor || '#FF007F'
            });
        }
    };

    // Crear un tema en blanco nuevo
    const handleCreateNewTheme = () => {
        const newTheme = {
            ...DEFAULT_THEME_TEMPLATE,
            id: null,
            name: `Tema Personalizado ${themesList.length + 1}`
        };
        setSelectedThemeId(null);
        setSelectedThemeState(newTheme);
        setActiveTab('general');
        if (typeof showMessage === 'function') {
            showMessage('Formulario preparado para registrar un nuevo tema.', MESSAGE_TYPE.INFO);
        }
    };

    // Actualizar campo de texto o color en el estado
    const handleFieldChange = (fieldName, fieldValue) => {
        setSelectedThemeState((prevState) => ({
            ...prevState,
            [fieldName]: fieldValue
        }));
    };

    // Subida de imagen individual a SkiaSharp
    const handleUploadAsset = async (file, assetType, fieldName) => {
        if (!file) return;

        setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
        try {
            const result = await UploadAsset(file, assetType);
            if (result.success && result.url) {
                setSelectedThemeState((prev) => ({
                    ...prev,
                    [fieldName]: result.url
                }));
                if (typeof showMessage === 'function') {
                    showMessage(`Recurso "${fieldName}" optimizado y cargado exitosamente.`, MESSAGE_TYPE.SUCCESS);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al subir el archivo.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (error) {
            if (typeof showMessage === 'function') {
                showMessage('Error inesperado durante la subida de imagen.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    // Eliminar individualmente una imagen asignada
    const handleRemoveAsset = (fieldName) => {
        setSelectedThemeState((prev) => ({
            ...prev,
            [fieldName]: ''
        }));
        if (typeof showMessage === 'function') {
            showMessage(`Recurso "${fieldName}" removido. Guarda los cambios para aplicar.`, MESSAGE_TYPE.INFO);
        }
    };

    // Guardar / Registrar tema (Create o Update)
    const handleSaveTheme = async () => {
        const themeName = (selectedThemeState.name || '').trim();
        if (!themeName) {
            if (typeof showMessage === 'function') {
                showMessage('El nombre del tema es obligatorio.', MESSAGE_TYPE.WARNING);
            }
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...selectedThemeState,
                name: themeName,
                roomId: roomId || null,
                operatorId: operatorId || null
            };

            let response;
            if (selectedThemeState.id) {
                // Actualizar tema existente
                response = await UpdateTheme(selectedThemeState.id, payload);
            } else {
                // Crear nuevo tema
                response = await CreateTheme(payload);
            }

            if (response.success && response.data) {
                if (typeof showMessage === 'function') {
                    showMessage(response.message || 'Tema guardado exitosamente.', MESSAGE_TYPE.SUCCESS);
                }
                const savedTheme = response.data;
                setSelectedThemeId(savedTheme.id);
                setSelectedThemeState({
                    ...DEFAULT_THEME_TEMPLATE,
                    ...savedTheme
                });

                if (typeof onThemesRefreshed === 'function') {
                    onThemesRefreshed(savedTheme.id);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(response.message || 'No se pudo guardar el tema.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (error) {
            if (typeof showMessage === 'function') {
                showMessage('Error de conexión al guardar el tema.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Clonar tema activo
    const handleCloneTheme = async () => {
        if (!selectedThemeState.id) {
            if (typeof showMessage === 'function') {
                showMessage('Debe seleccionar un tema existente para clonar.', MESSAGE_TYPE.WARNING);
            }
            return;
        }

        const cloneName = `${selectedThemeState.name} (Copia)`;
        setIsCloning(true);
        try {
            const result = await CloneTheme(selectedThemeState.id, cloneName);
            if (result.success && result.data) {
                if (typeof showMessage === 'function') {
                    showMessage(`Tema clonado como "${cloneName}" exitosamente.`, MESSAGE_TYPE.SUCCESS);
                }
                const clonedTheme = result.data;
                setSelectedThemeId(clonedTheme.id);
                setSelectedThemeState({
                    ...DEFAULT_THEME_TEMPLATE,
                    ...clonedTheme
                });

                if (typeof onThemesRefreshed === 'function') {
                    onThemesRefreshed(clonedTheme.id);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'Error al clonar el tema.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (error) {
            if (typeof showMessage === 'function') {
                showMessage('Error inesperado al clonar el tema.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsCloning(false);
        }
    };

    // Eliminar tema
    const handleDeleteTheme = async () => {
        if (!selectedThemeState.id) return;
        if (selectedThemeState.isSystemPreset) {
            if (typeof showMessage === 'function') {
                showMessage('Los temas preset del sistema están protegidos y no pueden eliminarse.', MESSAGE_TYPE.WARNING);
            }
            return;
        }

        const confirmDelete = window.confirm(`¿Estás seguro de eliminar permanentemente el tema "${selectedThemeState.name}"?`);
        if (!confirmDelete) return;

        setIsDeleting(true);
        try {
            const result = await DeleteTheme(selectedThemeState.id);
            if (result.success) {
                if (typeof showMessage === 'function') {
                    showMessage('Tema eliminado exitosamente.', MESSAGE_TYPE.SUCCESS);
                }
                if (typeof onThemesRefreshed === 'function') {
                    onThemesRefreshed(null);
                }
            } else {
                if (typeof showMessage === 'function') {
                    showMessage(result.message || 'No se pudo eliminar el tema.', MESSAGE_TYPE.ERROR);
                }
            }
        } catch (error) {
            if (typeof showMessage === 'function') {
                showMessage('Error al procesar la eliminación del tema.', MESSAGE_TYPE.ERROR);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    // Aplicar y seleccionar tema para la sala
    const handleApplyTheme = () => {
        if (selectedThemeState.id && typeof onThemeSelected === 'function') {
            onThemeSelected(selectedThemeState);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal fade show d-block surfer-theme-modal-backdrop"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(5px)', zIndex: 1060 }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark text-light border border-secondary border-opacity-50 shadow-lg">
                    
                    {/* Header del Modal */}
                    <div className="modal-header border-bottom border-secondary border-opacity-50 d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <i className="bi bi-palette2 text-info fs-3"></i>
                            <div>
                                <h5 className="modal-title fw-bold text-light mb-0">
                                    Personalizador de Temas: Esquivador (Surfer)
                                </h5>
                                <small className="text-secondary">
                                    Capas gráficas optimizadas con SkiaSharp a WebP (1920x1080 / 1024x1024 / 512x512)
                                </small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Cerrar"
                        ></button>
                    </div>

                    {/* Selector de Temas y Acciones de Cabecera */}
                    <div className="p-3 bg-black bg-opacity-40 border-bottom border-secondary border-opacity-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-12 col-md-5">
                                <label className="form-label text-light small fw-bold mb-1">
                                    Seleccionar Tema a Editar:
                                </label>
                                <select
                                    className="form-select form-select-sm bg-dark text-light border-secondary"
                                    value={selectedThemeId || ''}
                                    onChange={(e) => handleSelectThemeChange(e.target.value)}
                                >
                                    {themesList.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} {t.isSystemPreset ? '(Preset Sistema)' : ''}
                                        </option>
                                    ))}
                                    {!selectedThemeId && (
                                        <option value="">+ Nuevo Tema en Creación</option>
                                    )}
                                </select>
                            </div>
                            <div className="col-12 col-md-7 d-flex justify-content-md-end gap-2 flex-wrap mt-2 mt-md-0 align-items-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                                    onClick={handleCreateNewTheme}
                                >
                                    <i className="bi bi-plus-circle"></i> Nuevo Tema
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info d-flex align-items-center gap-1"
                                    onClick={handleCloneTheme}
                                    disabled={!selectedThemeState.id || isCloning}
                                >
                                    {isCloning ? (
                                        <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                        <i className="bi bi-copy"></i>
                                    )}
                                    Clonar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                    onClick={handleDeleteTheme}
                                    disabled={!selectedThemeState.id || selectedThemeState.isSystemPreset || isDeleting}
                                    title={selectedThemeState.isSystemPreset ? 'Los presets del sistema no pueden eliminarse' : 'Eliminar tema'}
                                >
                                    {isDeleting ? (
                                        <span className="spinner-border spinner-border-sm"></span>
                                    ) : (
                                        <i className="bi bi-trash"></i>
                                    )}
                                    Eliminar
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Navegación por Pestañas */}
                    <div className="px-3 pt-2 bg-dark border-bottom border-secondary border-opacity-50">
                        <ul className="nav nav-tabs border-0">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link text-light ${activeTab === 'general' ? 'active bg-black border-secondary border-bottom-0 text-info fw-bold' : 'border-0'}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    <i className="bi bi-brush me-1"></i> 1. Identidad & Marca
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link text-light ${activeTab === 'track' ? 'active bg-black border-secondary border-bottom-0 text-info fw-bold' : 'border-0'}`}
                                    onClick={() => setActiveTab('track')}
                                >
                                    <i className="bi bi-image me-1"></i> 2. Pista & Entorno
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link text-light ${activeTab === 'players' ? 'active bg-black border-secondary border-bottom-0 text-info fw-bold' : 'border-0'}`}
                                    onClick={() => setActiveTab('players')}
                                >
                                    <i className="bi bi-people me-1"></i> 3. Corredores (P1/P2)
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link text-light ${activeTab === 'collectibles' ? 'active bg-black border-secondary border-bottom-0 text-info fw-bold' : 'border-0'}`}
                                    onClick={() => setActiveTab('collectibles')}
                                >
                                    <i className="bi bi-coin me-1"></i> 4. Obstáculos & Items
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Cuerpo del Modal con Contenido de Pestañas */}
                    <div className="modal-body bg-black bg-opacity-25 p-4">
                        
                        {/* PESTAÑA 1: IDENTIDAD & MARCA */}
                        {activeTab === 'general' && (
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <label className="form-label fw-bold text-light small">
                                        Nombre del Tema <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control bg-dark text-light border-secondary"
                                        value={selectedThemeState.name || ''}
                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                        placeholder="Ej. Cyberpunk Neon Highway"
                                        maxLength={200}
                                    />
                                    {selectedThemeState.isSystemPreset && (
                                        <small className="text-warning d-block mt-1 font-monospace">
                                            <i className="bi bi-shield-lock me-1"></i> Preset oficial del sistema (Clona para editar y guardar cambios personalizados).
                                        </small>
                                    )}
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <label className="form-label fw-bold text-light small">
                                                Color Primario
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="color"
                                                    className="form-control form-control-color bg-dark border-secondary p-1"
                                                    value={selectedThemeState.primaryColor || '#00FFCC'}
                                                    onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control bg-dark text-light border-secondary font-monospace small"
                                                    value={selectedThemeState.primaryColor || '#00FFCC'}
                                                    onChange={(e) => handleFieldChange('primaryColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label fw-bold text-light small">
                                                Color Secundario
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="color"
                                                    className="form-control form-control-color bg-dark border-secondary p-1"
                                                    value={selectedThemeState.secondaryColor || '#FF007F'}
                                                    onChange={(e) => handleFieldChange('secondaryColor', e.target.value)}
                                                />
                                                <input
                                                    type="text"
                                                    className="form-control bg-dark text-light border-secondary font-monospace small"
                                                    value={selectedThemeState.secondaryColor || '#FF007F'}
                                                    onChange={(e) => handleFieldChange('secondaryColor', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 mt-3">
                                    <FileDropzone
                                        label="Logotipo de Marca / Patrocinador"
                                        fieldName="brandLogoUrl"
                                        assetType="brandLogo"
                                        value={selectedThemeState.brandLogoUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['brandLogoUrl'])}
                                        aspectHint="Recomendado: 512x256 PNG transparente"
                                        helpText="Se mostrará en la portada y cabeceras de la pista"
                                    />
                                </div>
                            </div>
                        )}

                        {/* PESTAÑA 2: PISTA & ENTORNO */}
                        {activeTab === 'track' && (
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <FileDropzone
                                        label="Fondo Panorámico / Cielo"
                                        fieldName="backgroundUrl"
                                        assetType="background"
                                        value={selectedThemeState.backgroundUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['backgroundUrl'])}
                                        aspectHint="Máx. 1920x1080 (16:9)"
                                        helpText="Capa de horizonte y fondo con efecto parallax"
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <FileDropzone
                                        label="Textura de Pista / Calzada"
                                        fieldName="roadUrl"
                                        assetType="road"
                                        value={selectedThemeState.roadUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['roadUrl'])}
                                        aspectHint="Máx. 1024x1024 (Textura repetible)"
                                        helpText="Superficie de los carriles sobre la que corren los jugadores"
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <FileDropzone
                                        label="Elementos Laterales / Props de Pista"
                                        fieldName="sidePropUrl"
                                        assetType="sideProp"
                                        value={selectedThemeState.sidePropUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['sidePropUrl'])}
                                        aspectHint="Máx. 1024x1024 PNG transparente"
                                        helpText="Postes, árboles, edificios o barreras al costado del camino"
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <FileDropzone
                                        label="Cabina / Cockpit HUD (Vista 1ra Persona)"
                                        fieldName="cockpitUrl"
                                        assetType="cockpit"
                                        value={selectedThemeState.cockpitUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['cockpitUrl'])}
                                        aspectHint="Máx. 1920x1080 PNG con transparencia"
                                        helpText="Marco de vehículo o parabrisas para vista inmersiva"
                                    />
                                </div>
                            </div>
                        )}

                        {/* PESTAÑA 3: CORREDORES 1 vs 1 */}
                        {activeTab === 'players' && (
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <div className="p-3 border border-secondary border-opacity-50 rounded-3 bg-dark">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span className="badge bg-danger">Jugador 1</span>
                                            <span className="text-light fw-bold small">Corredor Bando Izquierdo</span>
                                        </div>
                                        <FileDropzone
                                            label="Avatar / Vehículo Jugador 1"
                                            fieldName="player1Url"
                                            assetType="player1"
                                            value={selectedThemeState.player1Url || ''}
                                            onUpload={handleUploadAsset}
                                            onRemove={handleRemoveAsset}
                                            isUploading={Boolean(uploadingFields['player1Url'])}
                                            aspectHint="Máx. 512x512 PNG transparente"
                                            helpText="Sprite o modelo 2D/3D del corredor P1"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="p-3 border border-secondary border-opacity-50 rounded-3 bg-dark">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span className="badge bg-primary">Jugador 2</span>
                                            <span className="text-light fw-bold small">Corredor Bando Derecho</span>
                                        </div>
                                        <FileDropzone
                                            label="Avatar / Vehículo Jugador 2"
                                            fieldName="player2Url"
                                            assetType="player2"
                                            value={selectedThemeState.player2Url || ''}
                                            onUpload={handleUploadAsset}
                                            onRemove={handleRemoveAsset}
                                            isUploading={Boolean(uploadingFields['player2Url'])}
                                            aspectHint="Máx. 512x512 PNG transparente"
                                            helpText="Sprite o modelo 2D/3D del corredor P2"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PESTAÑA 4: OBSTÁCULOS & COLECCIONABLES */}
                        {activeTab === 'collectibles' && (
                            <div className="row g-3">
                                <div className="col-12 col-md-4">
                                    <FileDropzone
                                        label="Obstáculo de Pista"
                                        fieldName="obstacleUrl"
                                        assetType="obstacle"
                                        value={selectedThemeState.obstacleUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['obstacleUrl'])}
                                        aspectHint="Máx. 512x512 PNG transparente"
                                        helpText="Valla, barrera, roca o cono a esquivar"
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <FileDropzone
                                        label="Moneda / Coleccionable Estándar"
                                        fieldName="coinUrl"
                                        assetType="coin"
                                        value={selectedThemeState.coinUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['coinUrl'])}
                                        aspectHint="Máx. 512x512 PNG transparente"
                                        helpText="Otorga puntos estándar al recolectar (+50 pts)"
                                    />
                                </div>

                                <div className="col-12 col-md-4">
                                    <FileDropzone
                                        label="Bonus Especial / Nitro / Turbo"
                                        fieldName="bonusUrl"
                                        assetType="bonus"
                                        value={selectedThemeState.bonusUrl || ''}
                                        onUpload={handleUploadAsset}
                                        onRemove={handleRemoveAsset}
                                        isUploading={Boolean(uploadingFields['bonusUrl'])}
                                        aspectHint="Máx. 512x512 PNG transparente"
                                        helpText="Otorga gran bonificación al recolectar (+500 pts)"
                                    />
                                </div>
                            </div>
                        )}

                    </div>

                    {/* Footer del Modal */}
                    <div className="modal-footer border-top border-secondary border-opacity-50 d-flex justify-content-between">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>

                        <div className="d-flex gap-2">
                            {selectedThemeState.id && (
                                <button
                                    type="button"
                                    className="btn btn-outline-info btn-sm"
                                    onClick={handleApplyTheme}
                                >
                                    <i className="bi bi-check2-circle me-1"></i> Usar este Tema en Sala
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn btn-info btn-sm text-dark fw-bold"
                                onClick={handleSaveTheme}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-save me-1"></i> Guardar Cambios
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

SurferThemeModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    themesList: PropTypes.arrayOf(PropTypes.object),
    activeThemeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roomId: PropTypes.string,
    operatorId: PropTypes.string,
    onThemeSelected: PropTypes.func,
    onThemesRefreshed: PropTypes.func
};

export default SurferThemeModal;
